import { createParams } from 'api/api-utils';
import { CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { Database } from 'shared/database.enum';
import { SubIndex } from 'shared/sub-index.enum';
import queryApi from '../query-api';
import { Animal } from './animal';
import { AnimalParents } from './animal-parents';
import { AnimalProfileEntry } from './animal-profile-entry';
import { addUnavailable } from './animal-utils';
import { Embryo } from './embryo';
import { GroupTrait } from './group-trait';
import { LambOutcome } from './lamb-outcome';
import { Mating } from './mating';
import { Measurement } from './measurement';
import { Pedigree } from './pedigree';
import { TraitObserved } from './trait-observed';

/**
 * Handles all requests to the `/animal` resource.
 */
class AnimalApi {
  private resource = 'animal';

  /**
   * Gets an animal for the given animal id.
   *
   * If ASBVs are provided, those will be returned in the response,
   * otherwise it uses the defaults.
   */
  getByKey = async (id: string, db: Database, subIndex?: SubIndex, asbvs?: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/${id}`,
      {
        params: createParams({
          dbType: db,
          scenario: subIndex,
          customASBVs: asbvs,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(Animal, response.data);
  }

  getByKeys = async (ids: string[], db: Database, subIndex?: SubIndex, asbvs?: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/${ids.toString()}`,
      {
        params: createParams({
          dbType: db,
          scenario: subIndex,
          customASBVs: asbvs,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(Animal, response.data);
  }

  /**
   * Gets the animal grouped traits.
   */
  getTraitGroups = async (id: string, db: Database, subIndex?: SubIndex, asbvs?: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/${id}/grouptraits`,
      {
        params: createParams({
          dbType: db,
          scenario: subIndex,
          customASBVs: asbvs,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(GroupTrait, response.data as any[]);
  }

  /**
   * Gets the animal pedigree details.
   */
  getPedigree = async (id: string, db: Database, subIndex?: SubIndex, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/${id}/pedigree`,
      {
        params: createParams({
          dbType: db,
          scenario: subIndex,
        }),
        cancelToken: token,
      },
    );

    let animal = plainToClass(Pedigree, response.data);

    // If animal has no sire or dam, add "unavailable" values.
    if (!animal.children.length) {
      animal = addUnavailable(animal);
    }

    return animal;
  }

  /**
   * Gets the animal pedigree Admin.
   */
  getPedigreeAdmin = async (id: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/pedigree/${id}`,
      {
        cancelToken: token,
      },
    );

    let animal = plainToClass(Pedigree, response.data);

    // If animal has no sire or dam, add "unavailable" values.
    if (!animal.children.length) {
      animal = addUnavailable(animal);
    }

    return animal;
  }

  getAnimalParents = async (animalIds: string, dbType: Database, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/parents`,
      {
        params: createParams({
          dbType,
          animalIds,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(AnimalParents, response.data as []);
  }

  getAnimalInfo = async (animalIds: string, dbType: Database, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/group`,
      {
        params: createParams({
          dbType,
          animalIds,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(AnimalParents, response.data as any[]);
  }

  getAnimalProfile = async (animalLpid: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/profile/${animalLpid}`,
      {
        cancelToken: token,
      },
    );
    return plainToClass(Animal, response.data);
  }

  getAnimalProfileEntries = async (animalLpid: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/profile-entries/${animalLpid}`,
      {
        cancelToken: token,
      },
    );
    return plainToClass(AnimalProfileEntry, response.data as any[]);
  }

  getAnimalMeasurements = async (animalLpid: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/measurements/${animalLpid}`,
      {
        cancelToken: token,
      },
    );
    return plainToClass(Measurement, response.data as any[]);
  }

  getAnimalMatings = async (animalLpid: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/mating/${animalLpid}`,
      {
        cancelToken: token,
      },
    );
    return plainToClass(Mating, response.data as any[]);
  }

  getAnimalEmbryos = async (animalLpid: string, matingId: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/embryo/${animalLpid}/${matingId}`,
      {
        cancelToken: token,
      },
    );
    return plainToClass(Embryo, response.data as any[]);
  }

  getAnimalLambOutcome = async (animalLpid: string, matingId: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/lamb-outcome/${animalLpid}/${matingId}`,
      {
        cancelToken: token,
      },
    );
    return plainToClass(LambOutcome, response.data as any[]);
  }

  /**
   * Gets the animal grouped traits.
   */
  getTraitObserved = async (id: string, db: Database, subIndex?: SubIndex, token?: CancelToken) => {
    const response = await queryApi.get(
      `/${this.resource}/${id}/traits-observed`,
      {
        params: createParams({
          dbType: db,
          scenario: subIndex,
        }),
        cancelToken: token,
      },
    );
    return plainToClass(TraitObserved, response.data as any[]);
  }
}

export default new AnimalApi();
